import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, ApiError, type SignupRequest } from '../lib/api.js';
import { useFormatters, useI18n } from '../i18n/LanguageProvider.js';
import { Icon } from './Icons.js';

/**
 * The queue of people asking for an account.
 *
 * Approving is the only place in this system where one click creates a tenant,
 * a user and an email in one go, so the row shows everything a reviewer needs
 * to decide — organisation, person, phone, when they asked — rather than making
 * them open a detail view to find out who they are approving.
 */
export function SignupQueue() {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const formatters = useFormatters();
  const [notice, setNotice] = useState<{
    kind: 'ok' | 'error';
    text: string;
  } | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [plans, setPlans] = useState<Record<string, string>>({});

  const signups = useQuery({
    queryKey: ['admin-signups'],
    queryFn: () => api.adminSignups(),
  });
  const planList = useQuery({
    queryKey: ['admin-plans'],
    queryFn: api.adminPlans,
  });

  const settle = (text: string) => {
    setNotice({ kind: 'ok', text });
    void queryClient.invalidateQueries({ queryKey: ['admin-signups'] });
    // A new tenant changes the platform counters behind the dashboard tab.
    void queryClient.invalidateQueries({ queryKey: ['admin-stats'] });
  };

  const fail = (error: unknown) =>
    setNotice({
      kind: 'error',
      text: error instanceof ApiError ? error.message : t('common.requestFailed'),
    });

  const approve = useMutation({
    mutationFn: ({ id, planCode, note }: { id: string; planCode: string; note?: string }) =>
      api.approveSignup(id, planCode, note),
    onSuccess: () => settle(t('admin.signup.approved')),
    onError: fail,
  });

  const reject = useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) => api.rejectSignup(id, note),
    onSuccess: () => settle(t('admin.signup.rejected')),
    onError: fail,
  });

  if (signups.isLoading) return <div className="loading">{t('common.loading')}</div>;

  const pending = (signups.data?.signups ?? []).filter(
    (row: SignupRequest) => row.status === 'PENDING',
  );
  const decided = (signups.data?.signups ?? []).filter(
    (row: SignupRequest) => row.status !== 'PENDING',
  );

  const defaultPlan = planList.data?.plans.find((plan) => plan.isActive)?.code ?? '';

  return (
    <>
      <p className="muted small">{t('admin.signupsHint')}</p>
      {notice && (
        <div className={notice.kind === 'ok' ? 'notice ok' : 'notice error'}>{notice.text}</div>
      )}

      {pending.length === 0 && <p className="muted">{t('admin.signup.none')}</p>}

      {pending.map((row) => (
        <article key={row.id} className="card">
          <header className="card-head">
            <h3>{row.organisationName}</h3>
            <span className="badge">{formatters.date(row.createdAt)}</span>
          </header>

          <div className="detail-grid">
            <div>
              <span className="muted small">{t('admin.signup.contact')}</span>
              <div>{row.contactName}</div>
            </div>
            <div>
              <span className="muted small">{t('login.email')}</span>
              <div className="ltr mono small">{row.email}</div>
            </div>
            <div>
              <span className="muted small">{t('admin.signup.phone')}</span>
              <div className="ltr mono small">{row.phone}</div>
            </div>
          </div>

          <label htmlFor={`plan-${row.id}`}>{t('admin.signup.choosePlan')}</label>
          <select
            id={`plan-${row.id}`}
            value={plans[row.id] ?? defaultPlan}
            onChange={(event) => setPlans({ ...plans, [row.id]: event.target.value })}
          >
            {planList.data?.plans
              .filter((plan) => plan.isActive)
              .map((plan) => (
                <option key={plan.id} value={plan.code}>
                  {plan.name}
                </option>
              ))}
          </select>

          <label htmlFor={`note-${row.id}`}>{t('admin.signup.note')}</label>
          <input
            id={`note-${row.id}`}
            value={notes[row.id] ?? ''}
            onChange={(event) => setNotes({ ...notes, [row.id]: event.target.value })}
          />

          <div className="actions">
            <button
              type="button"
              className="primary small"
              disabled={approve.isPending || !planList.data}
              onClick={() =>
                approve.mutate({
                  id: row.id,
                  planCode: plans[row.id] ?? defaultPlan,
                  note: notes[row.id],
                })
              }
            >
              <Icon.check />
              {t('admin.signup.approve')}
            </button>
            <button
              type="button"
              className="danger small"
              disabled={reject.isPending}
              onClick={() => reject.mutate({ id: row.id, note: notes[row.id] })}
            >
              {t('admin.signup.reject')}
            </button>
          </div>
        </article>
      ))}

      {decided.length > 0 && (
        <>
          <h3 style={{ marginTop: 28 }}>{t('admin.signup.history')}</h3>
          <div className="grid-wrap">
            <table className="grid">
              <thead>
                <tr>
                  <th>{t('admin.signup.organisation')}</th>
                  <th>{t('login.email')}</th>
                  <th>{t('common.status')}</th>
                  <th>{t('admin.signup.requested')}</th>
                </tr>
              </thead>
              <tbody>
                {decided.map((row) => (
                  <tr key={row.id}>
                    <td>{row.organisationName}</td>
                    <td className="ltr mono small">{row.email}</td>
                    <td>
                      <span className={`badge ${row.status === 'APPROVED' ? 'ok' : 'warn'}`}>
                        {row.status}
                      </span>
                    </td>
                    <td className="small muted">{formatters.date(row.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}
